import styles from "./SectionLabel.module.css";
import { Text } from "./Text";

export interface SectionLabelProps {
  readonly title: string;
  /** Trailing note — a count, a state word, a unit. Never an action. */
  readonly note?: string;
  /**
   * Heading level. Sections inside a destination are level 2 by default; a
   * label nested inside another labelled section passes 3. The level is a prop
   * rather than derived from nesting because the correct answer depends on the
   * document, and a component that guesses produces a heading outline that
   * skips levels — which is worse for a screen-reader user than no headings.
   */
  readonly level?: 2 | 3 | 4;
  readonly id?: string;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * A section header: title plus an optional trailing note.
 *
 * It carries the heading trait, which is what makes a destination navigable by
 * heading rotor. The note is inside the heading on purpose — it is part of the
 * section's identity ("Saved workouts, 3"), and a note outside the heading is
 * read as unrelated body text immediately after it.
 */
export function SectionLabel({
  title,
  note,
  level = 2,
  id,
  className,
  testId,
}: SectionLabelProps) {
  const classes = [styles.row, className]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <div
      className={classes}
      role="heading"
      aria-level={level}
      id={id}
      data-testid={testId}
    >
      <Text variant="subheadline" weight="semibold">
        {title}
      </Text>
      {note !== undefined && note !== "" && (
        <Text variant="caption" tone="secondary" className={styles.note}>
          {note}
        </Text>
      )}
    </div>
  );
}
